import { IClauseType, IHighlightArea, IPolicyType } from "@/api";
import { styles } from "@/config/project";
import { findHighlightAreaOverlaps, IHighlightOverlap } from "@/utils";
import {
    HighlightArea,
    RenderHighlightsProps,
} from "@react-pdf-viewer/highlight";
import "@react-pdf-viewer/highlight/lib/styles/index.css";
import { memo, useCallback, useMemo, useState } from "react";
import {
    FoundToggleState,
    IGroupedScopeClause,
    IOverlappingClauseInfo,
    IScopeClauseWithTopic,
    ScopeDetailsTab,
} from "../../types";
import { HighlightTooltip } from "./HighlightTooltip";
import { OverlappingClausesPopover } from "./OverlappingClausesPopover";

const OVERLAP_HIGHLIGHT_COLOR = styles.primaryColor;

interface IProps extends RenderHighlightsProps {
    /**
     * The grouped scope clauses
     */
    groupedScopeClauses: IGroupedScopeClause[];
    /**
     * Function that handles clicking on a highlighted clauses
     */
    setExpandedFoundClause: (expanded: string | false) => void;
    /**
     * The currently expanded/selected clause panel ID
     */
    expandedFoundClause: string | false;
    setTab: (tab: ScopeDetailsTab) => void;
    setClausesFoundToggle: (toggle: FoundToggleState) => void;
}

/**
 * Custom component to render highlights for scope clauses
 */
export const ClauseHighlights = memo((props: IProps) => {
    const {
        groupedScopeClauses,
        setExpandedFoundClause,
        expandedFoundClause,
        setTab,
        setClausesFoundToggle,
        ...rest
    } = props;

    // State for the popover
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
    const [overlappingClauses, setOverlappingClauses] = useState<
        IOverlappingClauseInfo[]
    >([]);
    const open = Boolean(anchorEl);

    // Include groups where at least one scopeClause is found (has highlights)
    // For V2 scopes, same clause can appear in multiple topics with different found states
    const foundClauses = useMemo(
        () =>
            groupedScopeClauses.filter(({ scopeClauses }) =>
                scopeClauses.some(({ found }) => found)
            ),
        [groupedScopeClauses]
    );

    // Calculate overlaps for the current page
    const pageOverlaps = useMemo(
        () => findHighlightAreaOverlaps(groupedScopeClauses, props.pageIndex),
        [groupedScopeClauses, props.pageIndex]
    );

    // Create a map to quickly check if a specific highlight area overlaps
    const overlappingAreasMap = useMemo(() => {
        const map = new Map<string, IHighlightOverlap[]>();

        pageOverlaps.forEach((overlap) => {
            const key1 = `${overlap.area1.scope_clause_id}-${overlap.area1.page_index}-${overlap.area1.left}-${overlap.area1.top}`;
            const key2 = `${overlap.area2.scope_clause_id}-${overlap.area2.page_index}-${overlap.area2.left}-${overlap.area2.top}`;

            if (!map.has(key1)) map.set(key1, []);
            if (!map.has(key2)) map.set(key2, []);

            map.get(key1)!.push(overlap);
            map.get(key2)!.push(overlap);
        });

        return map;
    }, [pageOverlaps]);

    // Create a map to get clause info by scope clause ID (includes topic info for V2)
    const clauseInfoMap = useMemo(() => {
        const map = new Map<
            number,
            {
                clauseId: number;
                clauseTitle: string;
                highlightColor: string;
                policyType: IPolicyType;
                clauseTypes: IClauseType[];
                topicName?: string | null;
            }
        >();

        groupedScopeClauses.forEach((group) => {
            group.scopeClauses.forEach((scopeClause) => {
                map.set(scopeClause.id, {
                    clauseId: group.clause.id,
                    clauseTitle: group.clause.title,
                    highlightColor: group.highlightColor,
                    policyType: group.clause.policy_type,
                    clauseTypes: group.clause.clause_types,
                    topicName: scopeClause.scopeTopicName,
                });
            });
        });

        return map;
    }, [groupedScopeClauses]);

    /**
     * Checks if a specific highlight area overlaps with highlights from different scope clauses
     */
    const getHighlightOverlapInfo = useCallback(
        (scopeClauseId: number, area: IHighlightArea) => {
            const areaKey = `${scopeClauseId}-${area.page_index}-${area.left}-${area.top}`;
            return overlappingAreasMap.get(areaKey) || [];
        },
        [overlappingAreasMap]
    );

    // Create unique highlight areas to prevent stacking
    // Tracks all scope clause IDs at each location for same-clause-multiple-topics handling
    const uniqueHighlightAreas = useMemo(() => {
        const processedAreas = new Set<string>();
        const areas: Array<{
            area: IHighlightArea;
            scopeClause: IScopeClauseWithTopic;
            highlightColor: string;
            overlaps: IHighlightOverlap[];
            isOverlapping: boolean;
            allClauseIds: number[];
            // Track all scope clauses at this location (for same-clause-multiple-topics)
            allScopeClauses: IScopeClauseWithTopic[];
        }> = [];

        foundClauses.forEach((clause) => {
            clause.scopeClauses.forEach((scopeClause) => {
                if (!scopeClause.found) return; // Skip not-found clauses

                scopeClause.highlight_areas
                    .filter((area) => area.page_index === props.pageIndex)
                    .forEach((area) => {
                        const areaKey = `${area.page_index}-${area.left}-${area.top}-${area.width}-${area.height}`;

                        // Check if this area is already processed
                        const existingArea = areas.find((a) => {
                            return (
                                a.area.page_index === area.page_index &&
                                a.area.left === area.left &&
                                a.area.top === area.top &&
                                a.area.width === area.width &&
                                a.area.height === area.height
                            );
                        });

                        if (existingArea) {
                            // Only add scope clause if not already present
                            // (avoids duplicates when same scopeClause has multiple highlight_areas with same geometry)
                            const alreadyAdded =
                                existingArea.allScopeClauses.some(
                                    (sc) => sc.id === scopeClause.id
                                );
                            if (!alreadyAdded) {
                                existingArea.allScopeClauses.push(scopeClause);
                                // Also track unique clause IDs for backward compatibility
                                if (
                                    !existingArea.allClauseIds.includes(
                                        scopeClause.clause.id
                                    )
                                ) {
                                    existingArea.allClauseIds.push(
                                        scopeClause.clause.id
                                    );
                                }
                                // Mark as overlapping if multiple scope clauses at same location
                                if (existingArea.allScopeClauses.length > 1) {
                                    existingArea.isOverlapping = true;
                                }
                            }
                        } else if (!processedAreas.has(areaKey)) {
                            // First time seeing this area
                            processedAreas.add(areaKey);

                            const overlaps = getHighlightOverlapInfo(
                                scopeClause.id,
                                area
                            );
                            const isOverlapping = overlaps.length > 0;

                            areas.push({
                                area,
                                scopeClause,
                                highlightColor: clause.highlightColor,
                                overlaps,
                                isOverlapping,
                                allClauseIds: [scopeClause.clause.id],
                                allScopeClauses: [scopeClause],
                            });
                        }
                    });
            });
        });

        return areas;
    }, [foundClauses, props.pageIndex, getHighlightOverlapInfo]);

    const highlightAreasByScope = useMemo(() => {
        const groupedAreas = new Map<string, typeof uniqueHighlightAreas>();

        uniqueHighlightAreas.forEach((area) => {
            const scopeClauseId = area.scopeClause.id;
            const isOverlapping = area.isOverlapping;

            // For overlapping areas, group by the unique set of overlapping clause IDs
            // This ensures areas with the same overlaps are grouped together
            let groupKey: string;
            if (isOverlapping && area.overlaps.length > 0) {
                const overlappingClauseIds = new Set<number>();
                area.overlaps.forEach((overlap) => {
                    overlappingClauseIds.add(overlap.area1.clause_id);
                    overlappingClauseIds.add(overlap.area2.clause_id);
                });
                const sortedClauseIds = Array.from(overlappingClauseIds).sort();
                groupKey = `${scopeClauseId}-overlapping-${sortedClauseIds.join(",")}`;
            } else {
                // For non-overlapping areas, group by scope clause as before
                groupKey = `${scopeClauseId}-normal`;
            }

            if (!groupedAreas.has(groupKey)) {
                groupedAreas.set(groupKey, []);
            }
            groupedAreas.get(groupKey)!.push(area);
        });

        return groupedAreas;
    }, [uniqueHighlightAreas]);

    const jumpToClause = useCallback(
        (panelId: string) => {
            setExpandedFoundClause(panelId);

            // Set tab to clauses and toggle clauses found state
            setTab(ScopeDetailsTab.CLAUSES);
            setClausesFoundToggle(FoundToggleState.FOUND);
        },
        [setClausesFoundToggle, setExpandedFoundClause, setTab]
    );

    const handleClosePopover = useCallback(() => {
        setAnchorEl(null);
        setOverlappingClauses([]);
    }, []);

    const handleOverlapClick = useCallback(
        (
            event: React.MouseEvent<HTMLDivElement>,
            overlaps: IHighlightOverlap[],
            allScopeClauses: IScopeClauseWithTopic[]
        ) => {
            event.stopPropagation();

            // Use scope_clause_id for uniqueness (not clause_id)
            // This properly handles same-clause-multiple-topics scenarios
            const uniqueScopeClauseIds = new Set<number>();
            const clauseInfos: IOverlappingClauseInfo[] = [];

            // First, add all scope clauses at this exact location (same-clause-multiple-topics)
            allScopeClauses.forEach((scopeClause) => {
                if (!uniqueScopeClauseIds.has(scopeClause.id)) {
                    uniqueScopeClauseIds.add(scopeClause.id);
                    const clauseInfo = clauseInfoMap.get(scopeClause.id);
                    if (clauseInfo) {
                        clauseInfos.push({
                            scopeClauseId: scopeClause.id,
                            clauseId: scopeClause.clause.id,
                            clauseTitle: clauseInfo.clauseTitle,
                            highlightColor: clauseInfo.highlightColor,
                            policyType: clauseInfo.policyType,
                            clauseTypes: clauseInfo.clauseTypes,
                            topicName: clauseInfo.topicName,
                        });
                    }
                }
            });

            // Then add any additional overlapping clauses (different clauses that overlap)
            overlaps.forEach((overlap) => {
                [overlap.area1, overlap.area2].forEach((area) => {
                    if (!uniqueScopeClauseIds.has(area.scope_clause_id)) {
                        uniqueScopeClauseIds.add(area.scope_clause_id);
                        const clauseInfo = clauseInfoMap.get(
                            area.scope_clause_id
                        );
                        if (clauseInfo) {
                            clauseInfos.push({
                                scopeClauseId: area.scope_clause_id,
                                clauseId: area.clause_id,
                                clauseTitle: clauseInfo.clauseTitle,
                                highlightColor: clauseInfo.highlightColor,
                                policyType: clauseInfo.policyType,
                                clauseTypes: clauseInfo.clauseTypes,
                                topicName: clauseInfo.topicName,
                            });
                        }
                    }
                });
            });

            setOverlappingClauses(clauseInfos);
            setAnchorEl(event.currentTarget);
        },
        [clauseInfoMap]
    );

    const handleClauseSelect = useCallback(
        (scopeClauseId: number) => {
            jumpToClause(`panel${scopeClauseId}`);
            handleClosePopover();
        },
        [jumpToClause, handleClosePopover]
    );

    return (
        <div>
            {Array.from(highlightAreasByScope.entries()).map(
                ([groupKey, areas]) => {
                    const firstArea = areas[0];
                    const scopeClause = firstArea.scopeClause;

                    // Use overlaps and allScopeClauses from the first area
                    const groupOverlaps = firstArea.overlaps;
                    const groupAllScopeClauses = firstArea.allScopeClauses;

                    return (
                        <HighlightTooltip
                            key={`group-${groupKey}`}
                            overlaps={groupOverlaps}
                            scopeClause={scopeClause}
                            allScopeClauses={groupAllScopeClauses}
                        >
                            <div>
                                {areas.map(
                                    (
                                        {
                                            area,
                                            scopeClause: areaScopeClause,
                                            highlightColor,
                                            overlaps,
                                            isOverlapping,
                                            allScopeClauses,
                                        },
                                        index
                                    ) => {
                                        const highlightArea: HighlightArea = {
                                            height: area.height,
                                            left: area.left,
                                            pageIndex: area.page_index,
                                            top: area.top,
                                            width: area.width,
                                        };

                                        // Check if any scope clause at this location is expanded
                                        const isAnyClauseExpanded =
                                            allScopeClauses.some(
                                                (sc) =>
                                                    expandedFoundClause ===
                                                    `panel${sc.id}`
                                            );

                                        return (
                                            <div
                                                key={`highlight-${groupKey}-${index}-${area.left}-${area.top}`}
                                                className={`highlight-area ${isOverlapping ? "highlight-overlapping" : ""}`}
                                                onClick={(event) =>
                                                    isOverlapping
                                                        ? handleOverlapClick(
                                                              event,
                                                              overlaps,
                                                              allScopeClauses
                                                          )
                                                        : jumpToClause(
                                                              `panel${areaScopeClause.id}`
                                                          )
                                                }
                                                style={Object.assign(
                                                    {},
                                                    {
                                                        background:
                                                            isOverlapping
                                                                ? OVERLAP_HIGHLIGHT_COLOR
                                                                : highlightColor,
                                                        opacity:
                                                            isAnyClauseExpanded
                                                                ? 0.2
                                                                : 0.1,
                                                        zIndex: 10,
                                                        cursor: "pointer",
                                                        mixBlendMode: "normal",
                                                        pointerEvents: "auto",
                                                        isolation: "isolate",
                                                        transition:
                                                            "opacity 0.2s ease-in-out",
                                                    },
                                                    props.getCssProperties(
                                                        highlightArea,
                                                        rest.rotation
                                                    )
                                                )}
                                            />
                                        );
                                    }
                                )}
                            </div>
                        </HighlightTooltip>
                    );
                }
            )}

            {/* Overlapping clauses popover */}
            <OverlappingClausesPopover
                open={open}
                anchorEl={anchorEl}
                overlappingClauses={overlappingClauses}
                handleClosePopover={handleClosePopover}
                handleClauseSelect={handleClauseSelect}
            />
        </div>
    );
});
